import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { fmt } from '../../lib/constants';
import toast from 'react-hot-toast';

// ── UPDATED: All income types from Sunday Received Income Analysis Template ──
// Image 3 columns: Offerings, Tithes, Thanks Giving, Effort Day's,
// Foundation Day, Redem, Church School, Gifts, Others
const CONTRIBUTION_TYPES = [
  'Tithe',
  'Offering',
  'Thanksgiving',
  'Effort Day',       // ← from template: "Effort Day's"
  'Foundation Day',   // ← from template: "Foundation Day"
  'Redemption',       // ← from template: "Redem"
  'Church School',    // ← from template: "Church School"
  'Gifts',            // ← from template: "Gifts"
  'Harvest',
  'Building Fund',
  'Special',
  'Others',
];

const PAYMENT_METHODS = ['cash', 'mpesa', 'bank_transfer', 'cheque', 'card'];
const PM_LABELS = {
  cash:          '💵 Cash',
  mpesa:         '📱 M-Pesa',
  bank_transfer: '🏦 Bank Transfer',
  cheque:        '📃 Cheque',
  card:          '💳 Card',
};

export default function RecordContributionPage() {
  const { profile } = useAuth();
  const [mode,    setMode]    = useState('member');
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const blankShared = {
    date: today, type: 'Offering', amount: '',
    payment_method: 'cash', reference_no: '', notes: '',
  };

  // ── Per member ────────────────────────────────────────────────
  const [memberForm,    setMemberForm]    = useState({ ...blankShared });
  const [memberSearch,  setMemberSearch]  = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selMember,     setSelMember]     = useState(null);

  // ── Per district ──────────────────────────────────────────────
  const [distForm,    setDistForm]    = useState({ ...blankShared, district_id: '' });
  const [districtList,setDistrictList]= useState([]);

  // ── Sunday total ──────────────────────────────────────────────
  const [sundayForm, setSundayForm] = useState({ ...blankShared });

  useEffect(() => {
    supabase.from('districts').select('*').order('name')
      .then(({ data }) => setDistrictList(data || []));
  }, []);

  // ── Member search ─────────────────────────────────────────────
  async function searchMembers(val) {
    setMemberSearch(val);
    if (val.length < 2) { setMemberResults([]); return; }
    const { data } = await supabase.from('members')
      .select('id, first_name, last_name, member_number, districts:district_id(name), membership_type')
      .or(`first_name.ilike.%${val}%,last_name.ilike.%${val}%,member_number.ilike.%${val}%,phone.ilike.%${val}%`)
      .eq('status', 'active')
      .limit(6);
    setMemberResults(data || []);
  }

  function selectMember(m) {
    setSelMember(m);
    setMemberSearch(`${m.first_name} ${m.last_name}`);
    setMemberResults([]);
  }

  function reset() {
    setSelMember(null); setMemberSearch(''); setMemberResults([]);
    setMemberForm({ ...blankShared });
    setDistForm({ ...blankShared, district_id: '' });
    setSundayForm({ ...blankShared });
    setSuccess(null);
  }

  // ── Save: per member ──────────────────────────────────────────
  async function saveMember(e) {
    e.preventDefault();
    if (!selMember) { toast.error('Please select a member first.'); return; }
    setSaving(true);
    const { error } = await supabase.from('contributions').insert({
      entry_type:     'member',
      member_id:      selMember.id,
      date:           memberForm.date,
      type:           memberForm.type,
      amount:         Number(memberForm.amount),
      payment_method: memberForm.payment_method,
      reference_no:   memberForm.reference_no || '',
      notes:          memberForm.notes || '',
      recorded_by:    profile?.id,
    });
    if (error) { toast.error(error.message); setSaving(false); return; }
    setSuccess({
      mode: 'member',
      label: `${selMember.first_name} ${selMember.last_name} — ${memberForm.type} — KES ${Number(memberForm.amount).toLocaleString()}`,
    });
    reset(); setSaving(false);
  }

  // ── Save: per district ────────────────────────────────────────
  async function saveDistrict(e) {
    e.preventDefault();
    if (!distForm.district_id) { toast.error('Please select a district.'); return; }
    setSaving(true);
    const distName = districtList.find(d => d.id === distForm.district_id)?.name || '';
    const { error } = await supabase.from('contributions').insert({
      entry_type:     'district',
      member_id:      null,
      district_id:    distForm.district_id,
      date:           distForm.date,
      type:           distForm.type,
      amount:         Number(distForm.amount),
      payment_method: distForm.payment_method,
      reference_no:   distForm.reference_no || '',
      notes:          distForm.notes || '',
      recorded_by:    profile?.id,
    });
    if (error) { toast.error(error.message); setSaving(false); return; }
    setSuccess({
      mode: 'district',
      label: `${distName} District — ${distForm.type} — KES ${Number(distForm.amount).toLocaleString()}`,
    });
    reset(); setSaving(false);
  }

  // ── Save: sunday total ────────────────────────────────────────
  async function saveSunday(e) {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.from('contributions').insert({
      entry_type:     'sunday_total',
      member_id:      null,
      district_id:    null,
      service_date:   sundayForm.date,
      date:           sundayForm.date,
      type:           sundayForm.type,
      amount:         Number(sundayForm.amount),
      payment_method: sundayForm.payment_method,
      reference_no:   sundayForm.reference_no || '',
      notes:          sundayForm.notes || '',
      recorded_by:    profile?.id,
    });
    if (error) { toast.error(error.message); setSaving(false); return; }
    setSuccess({
      mode: 'sunday_total',
      label: `Sunday ${sundayForm.date} — ${sundayForm.type} (Church Total) — KES ${Number(sundayForm.amount).toLocaleString()}`,
    });
    reset(); setSaving(false);
  }

  const setM = (k) => (e) => setMemberForm(f => ({ ...f, [k]: e.target.value }));
  const setD = (k) => (e) => setDistForm(f => ({ ...f, [k]: e.target.value }));
  const setS = (k) => (e) => setSundayForm(f => ({ ...f, [k]: e.target.value }));

  // ── Shared payment fields ─────────────────────────────────────
  const PaymentFields = ({ form, setter }) => (
    <div className="grid-2">
      <div className="form-group">
        <label className="form-label">Payment Method</label>
        <select className="form-select" value={form.payment_method} onChange={setter('payment_method')}>
          {PAYMENT_METHODS.map(m => <option key={m} value={m}>{PM_LABELS[m]}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Reference / Receipt No.</label>
        <input className="form-input" value={form.reference_no} onChange={setter('reference_no')} placeholder="M-Pesa ref, cheque no…"/>
      </div>
      <div className="form-group" style={{ gridColumn: '1/-1' }}>
        <label className="form-label">Notes (optional)</label>
        <input className="form-input" value={form.notes} onChange={setter('notes')} placeholder="Any additional notes…"/>
      </div>
    </div>
  );

  // ── Type selector with grouping ───────────────────────────────
  const TypeSelect = ({ value, onChange }) => (
    <div className="form-group">
      <label className="form-label">Contribution Type *</label>
      <select className="form-select" value={value} onChange={onChange}>
        <optgroup label="── Regular ──────────────">
          <option>Tithe</option>
          <option>Offering</option>
          <option>Thanksgiving</option>
        </optgroup>
        <optgroup label="── Special Collections ──">
          <option>Effort Day</option>
          <option>Foundation Day</option>
          <option>Redemption</option>
          <option>Church School</option>
          <option>Gifts</option>
          <option>Harvest</option>
          <option>Building Fund</option>
          <option>Special</option>
        </optgroup>
        <optgroup label="── Other ────────────────">
          <option>Others</option>
        </optgroup>
      </select>
    </div>
  );

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="mb-4">
        <h1 style={{ fontSize: 20, fontWeight: 800 }}>💰 Record Contribution</h1>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>
          Choose how you want to record this contribution
        </div>
      </div>

      {/* Success banner */}
      {success && (
        <div style={{ background: '#E8F5E9', border: '1.5px solid #2E7D32', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#1B5E20', fontSize: 14 }}>✅ Contribution Recorded!</div>
            <div style={{ fontSize: 12, color: '#2E7D32', marginTop: 3 }}>{success.label}</div>
          </div>
          <button className="btn btn-sm" style={{ background: '#2E7D32', color: 'white', border: 'none' }} onClick={() => setSuccess(null)}>
            + Record Another
          </button>
        </div>
      )}

      {/* Mode selector */}
      <div className="card mb-4" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', background: 'var(--blue-dark)', color: 'white', fontWeight: 700, fontSize: 13 }}>
          📋 Select Recording Mode
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0 }}>
          {[
            { key: 'member',       icon: '👤', title: 'Per Member',   desc: 'Record for a specific member by name or number' },
            { key: 'district',     icon: '📍', title: 'Per District', desc: 'Record a district total — no individual member needed' },
            { key: 'sunday_total', icon: '⛪', title: 'Sunday Total', desc: 'Record the whole church total for a Sunday service' },
          ].map((opt, i) => (
            <button key={opt.key} onClick={() => setMode(opt.key)}
              style={{
                padding: '16px 14px', border: 'none',
                borderRight: i < 2 ? '1px solid var(--border)' : 'none',
                cursor: 'pointer', textAlign: 'left', transition: 'all .15s', fontFamily: 'inherit',
                background: mode === opt.key ? 'var(--blue-light)' : 'white',
                borderBottom: mode === opt.key ? '3px solid var(--blue)' : '3px solid transparent',
              }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{opt.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: mode === opt.key ? 'var(--blue-dark)' : 'var(--text)', marginBottom: 3 }}>
                {opt.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.4 }}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── PER MEMBER ── */}
      {mode === 'member' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 18px', background: 'var(--blue-light)', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--blue-dark)', fontWeight: 600 }}>
            👤 Recording contribution for a specific member
          </div>
          <div className="card-pad">
            <form onSubmit={saveMember}>
              <div className="form-group">
                <label className="form-label">Search Member *</label>
                <input className="form-input" value={memberSearch}
                  onChange={e => searchMembers(e.target.value)}
                  placeholder="Type name, member number or phone…"/>
                {memberResults.length > 0 && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, overflow: 'hidden', background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,.1)' }}>
                    {memberResults.map(m => (
                      <div key={m.id} onClick={() => selectMember(m)}
                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{m.first_name} {m.last_name}</span>
                          <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>{m.member_number}</span>
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{m.districts?.name} · {m.membership_type}</span>
                      </div>
                    ))}
                  </div>
                )}
                {selMember && (
                  <div style={{ marginTop: 6, padding: '9px 14px', background: 'var(--blue-light)', borderRadius: 8, fontSize: 12, color: 'var(--blue-dark)', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>✓ {selMember.first_name} {selMember.last_name} — {selMember.member_number} — {selMember.districts?.name}</span>
                    <button type="button" onClick={() => { setSelMember(null); setMemberSearch(''); }}
                      style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 16 }}>✕</button>
                  </div>
                )}
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={memberForm.date} onChange={setM('date')} required/>
                </div>
                <TypeSelect value={memberForm.type} onChange={setM('type')}/>
                <div className="form-group">
                  <label className="form-label">Amount (KES) *</label>
                  <input className="form-input" type="number" min="1" value={memberForm.amount} onChange={setM('amount')} placeholder="0" required/>
                </div>
              </div>
              <PaymentFields form={memberForm} setter={setM}/>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: 8 }}>
                {saving ? 'Saving…' : '💾 Save Member Contribution'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── PER DISTRICT ── */}
      {mode === 'district' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 18px', background: '#E8F5E9', borderBottom: '1px solid var(--border)', fontSize: 13, color: '#1B5E20', fontWeight: 600 }}>
            📍 Recording a district total — matches Sunday Received Income Analysis template
          </div>
          <div className="card-pad">
            <form onSubmit={saveDistrict}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={distForm.date} onChange={setD('date')} required/>
                </div>
                <div className="form-group">
                  <label className="form-label">District *</label>
                  <select className="form-select" value={distForm.district_id} onChange={setD('district_id')} required>
                    <option value="">Select district…</option>
                    {districtList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <TypeSelect value={distForm.type} onChange={setD('type')}/>
                <div className="form-group">
                  <label className="form-label">Total Amount (KES) *</label>
                  <input className="form-input" type="number" min="1" value={distForm.amount} onChange={setD('amount')} placeholder="Total collected from this district" required/>
                </div>
              </div>
              <PaymentFields form={distForm} setter={setD}/>
              <div style={{ background: '#F1F8E9', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#2E7D32' }}>
                💡 This records the total collected from the selected district — mirrors the physical Sunday Received Income Analysis template.
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: 8, background: '#2E7D32', borderColor: '#2E7D32' }}>
                {saving ? 'Saving…' : '💾 Save District Contribution'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── SUNDAY TOTAL ── */}
      {mode === 'sunday_total' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 18px', background: '#FFF8E0', borderBottom: '1px solid var(--border)', fontSize: 13, color: '#C47A00', fontWeight: 600 }}>
            ⛪ Recording whole-church total for a Sunday service
          </div>
          <div className="card-pad">
            <form onSubmit={saveSunday}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Sunday Date *</label>
                  <input className="form-input" type="date" value={sundayForm.date} onChange={setS('date')} required/>
                </div>
                <TypeSelect value={sundayForm.type} onChange={setS('type')}/>
                <div className="form-group">
                  <label className="form-label">Total Amount (KES) *</label>
                  <input className="form-input" type="number" min="1" value={sundayForm.amount} onChange={setS('amount')} placeholder="Total collected during this service" required/>
                </div>
              </div>
              <PaymentFields form={sundayForm} setter={setS}/>
              <div style={{ background: '#FFF8E0', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#C47A00' }}>
                💡 This records the church-wide total for this Sunday. Use district mode above to break it down per district.
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: 8, background: '#C47A00', borderColor: '#C47A00' }}>
                {saving ? 'Saving…' : '💾 Save Sunday Total'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
